import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_PRICES,
  nightDatesBetween,
  type AdminStayCreate,
  type AdminStayUpdate,
  type PricesUpdate,
} from '@baerenfang/shared';
import {
  ApiClientError,
  createAdminStay,
  deleteAdminStay,
  fetchAdminStayDetail,
  fetchAdminStays,
  getPrices,
  updateAdminStay,
  type AdminStayDetail,
  type AdminStayNight,
  type AdminStaySummary,
  type CapacityDetails,
} from '../../lib/api';
import { todayInZurich } from '../../lib/today';
import { addMonthsToKey, monthKey, firstOfMonth, lastOfMonth } from '../../lib/months';
import { chf, chfToRappen, formatDateShort, rappenToChfString } from '../../lib/format';
import { DateRangePicker } from '../../components/DateRangePicker';
import { useDateRange } from '../../lib/useDateRange';

const sumNight = (n: { members_adult: number; members_child: number; guests_adult: number; guests_child: number }) =>
  n.members_adult + n.members_child + n.guests_adult + n.guests_child;

export function AdminBookingsTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const today = todayInZurich();
  const thisMonth = monthKey(today);

  const { from, to, reset } = useDateRange({
    initialFrom: firstOfMonth(addMonthsToKey(thisMonth, -1)),
    initialTo: lastOfMonth(addMonthsToKey(thisMonth, 2)),
  });
  const [nameFilter, setNameFilter] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [rows, setRows] = useState<AdminStaySummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const ctl = new AbortController();
    setError(null);
    fetchAdminStays(from, to, ctl.signal)
      .then(setRows)
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
        else setError(t('errors.ERR_INTERNAL'));
      });
    return () => ctl.abort();
  }, [from, to, refreshKey, t]);

  const filtered = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (needle && !r.name.toLowerCase().includes(needle)) return false;
      if (unpaidOnly && r.paid_at != null) return false;
      return true;
    });
  }, [rows, nameFilter, unpaidOnly]);

  const totals = useMemo(() => {
    let stayover = 0;
    let beverages = 0;
    let members_adult = 0;
    let members_child = 0;
    let guests_adult = 0;
    let guests_child = 0;
    for (const r of filtered) {
      stayover += r.stayover_rappen;
      beverages += r.beverages_rappen;
      members_adult += r.sum_members_adult;
      members_child += r.sum_members_child;
      guests_adult += r.sum_guests_adult;
      guests_child += r.sum_guests_child;
    }
    return {
      stayover,
      beverages,
      total: stayover + beverages,
      members_adult,
      members_child,
      guests_adult,
      guests_child,
      persons: members_adult + members_child + guests_adult + guests_child,
    };
  }, [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium"
        >
          + {t('admin.bookings.newBooking')}
        </button>
      </div>

      <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-sm col-span-2 min-w-0">
          <span className="block mb-1 text-slate-600 dark:text-slate-400">
            {t('admin.calendar.from')} – {t('admin.calendar.to')}
          </span>
          <DateRangePicker from={from} to={to} onChange={reset} />
        </label>
        <label className="text-sm col-span-2">
          <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.bookings.filterName')}</span>
          <input
            type="search"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm col-span-2 sm:col-span-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={unpaidOnly}
            onChange={(e) => setUnpaidOnly(e.target.checked)}
          />
          <span>{t('admin.bookings.unpaidOnly')}</span>
        </label>
      </div>

      {error && (
        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {filtered.length > 0 && (
        <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg px-3 py-2 space-y-1 text-sm">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('admin.bookings.totals.stayover')}: </span>
              <span className="font-medium tabular-nums">{chf(totals.stayover, lang)}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('admin.bookings.totals.beverages')}: </span>
              <span className="font-medium tabular-nums">{chf(totals.beverages, lang)}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('admin.bookings.totals.total')}: </span>
              <span className="font-semibold tabular-nums">{chf(totals.total, lang)}</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 dark:border-slate-800 pt-1">
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('stayForm.cat.members_adult')}: </span>
              <span className="font-medium tabular-nums">{totals.members_adult}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('stayForm.cat.members_child')}: </span>
              <span className="font-medium tabular-nums">{totals.members_child}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('stayForm.cat.guests_adult')}: </span>
              <span className="font-medium tabular-nums">{totals.guests_adult}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('stayForm.cat.guests_child')}: </span>
              <span className="font-medium tabular-nums">{totals.guests_child}</span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">{t('admin.bookings.totals.persons')}: </span>
              <span className="font-semibold tabular-nums">{totals.persons}</span>
            </span>
          </div>
        </div>
      )}

      {/* Wide-screen table */}
      <div className="hidden sm:block bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="text-left p-2 font-medium">{t('admin.bookings.name')}</th>
              <th className="text-left p-2 font-medium">{t('stayForm.checkIn')}</th>
              <th className="text-left p-2 font-medium">{t('stayForm.checkOut')}</th>
              <th className="text-right p-2 font-medium">{t('admin.bookings.stayover')}</th>
              <th className="text-right p-2 font-medium">{t('admin.bookings.beverages')}</th>
              <th className="text-right p-2 font-medium">{t('admin.bookings.total')}</th>
              <th className="text-left p-2 font-medium">{t('admin.bookings.paid')}</th>
              <th className="text-left p-2 font-medium">{t('admin.bookings.editedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-slate-500 dark:text-slate-400">
                  {t('admin.bookings.empty')}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr
                key={r.id}
                onClick={() => setOpenId(r.id)}
                className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <td className="p-2 font-medium">
                  <span className="flex items-center gap-1.5">
                    <span>{r.name}{r.surname ? ` ${r.surname}` : ''}</span>
                    {r.is_external === 1 && (
                      <span
                        className="shrink-0 inline-flex items-center rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200 text-xs font-medium px-2 py-0.5"
                        title={t('admin.bookings.externalPillHelp')}
                      >
                        {t('admin.bookings.externalPill')}
                      </span>
                    )}
                  </span>
                </td>
                <td className="p-2">{formatDateShort(r.check_in, lang)}</td>
                <td className="p-2">{formatDateShort(r.check_out, lang)}</td>
                <td className="p-2 text-right tabular-nums">{chf(r.stayover_rappen, lang)}</td>
                <td className="p-2 text-right tabular-nums">{chf(r.beverages_rappen, lang)}</td>
                <td className="p-2 text-right tabular-nums font-medium">
                  {chf(r.stayover_rappen + r.beverages_rappen, lang)}
                </td>
                <td className="p-2">
                  {r.paid_at ? (
                    <span
                      className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 text-xs font-medium px-2 py-0.5"
                      title={t('admin.bookings.paidOn', { date: r.paid_at })}
                    >
                      ✓ {t('admin.bookings.paid')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200 text-xs font-medium px-2 py-0.5">
                      {t('admin.bookings.unpaid')}
                    </span>
                  )}
                </td>
                <td className="p-2 text-slate-600 dark:text-slate-400 text-xs">{r.edited_at ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Narrow-screen card list */}
      <div className="sm:hidden bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-800">
        {filtered.length === 0 && (
          <p className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
            {t('admin.bookings.empty')}
          </p>
        )}
        {filtered.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setOpenId(r.id)}
            className="w-full text-left p-3 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 dark:hover:bg-slate-800 dark:focus:bg-slate-800"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium truncate flex items-center gap-1.5">
                <span>{r.name}{r.surname ? ` ${r.surname}` : ''}</span>
                {r.is_external === 1 && (
                  <span
                    className="shrink-0 inline-flex items-center rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200 text-xs font-medium px-2 py-0.5"
                    title={t('admin.bookings.externalPillHelp')}
                  >
                    {t('admin.bookings.externalPill')}
                  </span>
                )}
              </span>
              {r.paid_at ? (
                <span
                  className="shrink-0 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 text-xs font-medium px-2 py-0.5"
                  title={t('admin.bookings.paidOn', { date: r.paid_at })}
                >
                  ✓ {t('admin.bookings.paid')}
                </span>
              ) : (
                <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200 text-xs font-medium px-2 py-0.5">
                  {t('admin.bookings.unpaid')}
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 tabular-nums">
              {formatDateShort(r.check_in, lang)} – {formatDateShort(r.check_out, lang)}
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 text-sm">
              <span className="text-slate-500 dark:text-slate-400 text-xs">
                {chf(r.stayover_rappen, lang)} · {chf(r.beverages_rappen, lang)}
              </span>
              <span className="font-medium tabular-nums">
                {chf(r.stayover_rappen + r.beverages_rappen, lang)}
              </span>
            </div>
          </button>
        ))}
      </div>

      {openId != null && (
        <EditDrawer
          id={openId}
          lang={lang}
          onClose={() => setOpenId(null)}
          onSaved={() => {
            setOpenId(null);
            refresh();
          }}
        />
      )}

      {addOpen && (
        <NewBookingDrawer
          lang={lang}
          today={today}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ─── edit drawer ────────────────────────────────────────────────────── */

function EditDrawer({
  id,
  lang,
  onClose,
  onSaved,
}: {
  id: number;
  lang: string;
  onClose(): void;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<AdminStayDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    fetchAdminStayDetail(id, ctl.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof ApiClientError) setLoadError(t(`errors.${e.code}`, e.message));
        else setLoadError(t('errors.ERR_INTERNAL'));
      });
    return () => ctl.abort();
  }, [id, t]);

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/40 dark:bg-black/60 flex items-stretch justify-end">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-medium">{t('admin.bookings.editStay')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-sm"
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </div>
        <div className="p-4">
          {!detail && !loadError && <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</p>}
          {loadError && (
            <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
              {loadError}
            </p>
          )}
          {detail && (
            <StayDrawerForm
              mode={{ kind: 'edit', detail }}
              lang={lang}
              onSaved={onSaved}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function NewBookingDrawer({
  lang,
  today,
  onClose,
  onSaved,
}: {
  lang: string;
  today: string;
  onClose(): void;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-30 bg-slate-900/40 dark:bg-black/60 flex items-stretch justify-end">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-medium">{t('admin.bookings.newBooking')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-2 py-1 text-sm"
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </div>
        <div className="p-4">
          <StayDrawerForm
            mode={{ kind: 'create', today }}
            lang={lang}
            onSaved={onSaved}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}

type StayDrawerMode =
  | { kind: 'edit'; detail: AdminStayDetail }
  | { kind: 'create'; today: string };

function blankNight(date: string): AdminStayNight {
  return {
    stay_id: 0,
    date,
    members_adult: 0,
    members_child: 0,
    guests_adult: 0,
    guests_child: 0,
  };
}

function StayDrawerForm({
  mode,
  lang,
  onSaved,
  onClose,
}: {
  mode: StayDrawerMode;
  lang: string;
  onSaved(): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const isEdit = mode.kind === 'edit';

  // Initial values. In create mode start with name empty, today + 1 night.
  const initialCheckIn = mode.kind === 'edit' ? mode.detail.check_in : mode.today;
  const initialCheckOut = mode.kind === 'edit'
    ? mode.detail.check_out
    : addDays(mode.today, 1);
  const initialNights =
    mode.kind === 'edit'
      ? mode.detail.nights
      : nightDatesBetween(initialCheckIn, initialCheckOut).map(blankNight);

  const [name, setName] = useState(mode.kind === 'edit' ? mode.detail.name : '');

  // External-booking contact block. Shown only when editing an `is_external`
  // row (those fields are NULL for roster rows so there's nothing to edit).
  const isExternal = mode.kind === 'edit' && mode.detail.is_external === 1;
  const [surname, setSurname] = useState(
    mode.kind === 'edit' ? (mode.detail.surname ?? '') : '',
  );
  const [street, setStreet] = useState(
    mode.kind === 'edit' ? (mode.detail.street ?? '') : '',
  );
  const [zipcode, setZipcode] = useState(
    mode.kind === 'edit' ? (mode.detail.zipcode ?? '') : '',
  );
  const [town, setTown] = useState(
    mode.kind === 'edit' ? (mode.detail.town ?? '') : '',
  );
  const [email, setEmail] = useState(
    mode.kind === 'edit' ? (mode.detail.email ?? '') : '',
  );

  const {
    from: checkIn,
    to: checkOut,
    reset: resetDates,
  } = useDateRange({
    initialFrom: initialCheckIn,
    initialTo: initialCheckOut,
    minSpanDays: 1,
  });
  const [nights, setNights] = useState<AdminStayNight[]>(initialNights);
  const [stayoverChf, setStayoverChf] = useState(
    mode.kind === 'edit' ? rappenToChfString(mode.detail.stayover_rappen) : '',
  );
  const [beveragesChf, setBeveragesChf] = useState(
    mode.kind === 'edit' ? rappenToChfString(mode.detail.beverages_rappen) : '',
  );
  const [notes, setNotes] = useState(mode.kind === 'edit' ? (mode.detail.notes ?? '') : '');
  const [override, setOverride] = useState(false);
  const [paid, setPaid] = useState(
    mode.kind === 'edit' ? mode.detail.paid_at != null : false,
  );
  const [payTwint, setPayTwint] = useState(
    mode.kind === 'edit' ? mode.detail.pay_twint !== 0 : false,
  );
  const [paySlip, setPaySlip] = useState(
    mode.kind === 'edit' ? mode.detail.pay_slip !== 0 : false,
  );
  const [payCash, setPayCash] = useState(
    mode.kind === 'edit' ? mode.detail.pay_cash !== 0 : false,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [capacityErrors, setCapacityErrors] = useState<CapacityDetails | null>(null);

  // Auto-calc Stayover from per-category headcount × prices. Same dirty-flag
  // pattern as the guest StayForm: in CREATE mode start unedited so the
  // suggestion fills as the admin enters counts; in EDIT mode start edited
  // so the saved booking value isn't blown away on mount. The admin can
  // always opt back into the suggestion via the "Vorschlag übernehmen" link.
  const [prices, setPrices] = useState<PricesUpdate>(DEFAULT_PRICES);
  const [stayoverEditedByUser, setStayoverEditedByUser] = useState(isEdit);

  useEffect(() => {
    const ctl = new AbortController();
    getPrices(ctl.signal)
      .then(setPrices)
      .catch(() => {
        // Keep DEFAULT_PRICES; admin can still type the amount manually.
      });
    return () => ctl.abort();
  }, []);

  // Inline (NOT memoised) so the up-to-date value is recomputed on every
  // render — same reasoning as in StayForm: react-hook-form mutates the
  // nights array in place, which would defeat a useMemo([nights]).
  let suggestedStayoverRappen = 0;
  for (const n of nights) {
    suggestedStayoverRappen +=
      (Number(n.members_adult) || 0) * prices.members_adult +
      (Number(n.members_child) || 0) * prices.members_child +
      (Number(n.guests_adult) || 0) * prices.guests_adult +
      (Number(n.guests_child) || 0) * prices.guests_child;
  }

  useEffect(() => {
    if (stayoverEditedByUser) return;
    if (nights.length === 0) return;
    setStayoverChf(rappenToChfString(suggestedStayoverRappen));
  }, [suggestedStayoverRappen, nights.length, stayoverEditedByUser]);

  const editTotalChf = useMemo(() => {
    const s = chfToRappen(stayoverChf);
    const b = chfToRappen(beveragesChf);
    if (!Number.isFinite(s) || !Number.isFinite(b)) return null;
    return chf(s + b, lang);
  }, [stayoverChf, beveragesChf, lang]);

  // Regenerate nights when dates change, preserving existing rows.
  useEffect(() => {
    if (!checkIn || !checkOut || checkIn >= checkOut) return;
    const dates = nightDatesBetween(checkIn, checkOut);
    setNights((existing) =>
      dates.map((d) => existing.find((n) => n.date === d) ?? blankNight(d)),
    );
  }, [checkIn, checkOut]);

  const setNightField = (date: string, key: keyof typeof nights[number], value: number) => {
    setNights((prev) => prev.map((n) => (n.date === date ? { ...n, [key]: value } : n)));
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    setCapacityErrors(null);

    if (mode.kind === 'create' && !name.trim()) {
      setErr(t('errors.ERR_VALIDATION'));
      setBusy(false);
      return;
    }

    const stayover_rappen = chfToRappen(stayoverChf || '0');
    const beverages_rappen = chfToRappen(beveragesChf || '0');
    if (!Number.isFinite(stayover_rappen) || !Number.isFinite(beverages_rappen)) {
      setErr(t('stayForm.invalidAmount'));
      setBusy(false);
      return;
    }

    const sharedPayload: AdminStayUpdate = {
      check_in: checkIn,
      check_out: checkOut,
      nights: nights.map((n) => ({
        date: n.date,
        members_adult: Number(n.members_adult) || 0,
        members_child: Number(n.members_child) || 0,
        guests_adult: Number(n.guests_adult) || 0,
        guests_child: Number(n.guests_child) || 0,
      })),
      stayover_rappen,
      beverages_rappen,
      notes: notes.trim() ? notes.trim() : null,
      pay_twint: payTwint,
      pay_slip: paySlip,
      pay_cash: payCash,
      // Only forward the contact block when editing an external booking;
      // for roster bookings these fields stay NULL in the DB.
      surname: isExternal ? (surname.trim() || null) : undefined,
      street: isExternal ? (street.trim() || null) : undefined,
      zipcode: isExternal ? (zipcode.trim() || null) : undefined,
      town: isExternal ? (town.trim() || null) : undefined,
      email: isExternal ? (email.trim() || null) : undefined,
      override_capacity: override,
      paid,
    };

    try {
      if (mode.kind === 'edit') {
        await updateAdminStay(mode.detail.id, sharedPayload);
      } else {
        const payload: AdminStayCreate = { name: name.trim(), ...sharedPayload };
        await createAdminStay(payload);
      }
      onSaved();
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'ERR_CAPACITY') {
        setCapacityErrors((e.details as CapacityDetails) ?? {});
        setErr(t('errors.ERR_CAPACITY'));
      } else if (e instanceof ApiClientError) {
        setErr(t(`errors.${e.code}`, e.message));
      } else {
        setErr(t('errors.ERR_INTERNAL'));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (mode.kind !== 'edit') return;
    if (!confirm(t('admin.bookings.confirmDelete', { name: mode.detail.name }))) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteAdminStay(mode.detail.id);
      onSaved();
    } catch (e) {
      if (e instanceof ApiClientError) setErr(t(`errors.${e.code}`, e.message));
      else setErr(t('errors.ERR_INTERNAL'));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {isEdit ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('admin.bookings.name')}:{' '}
          <span className="font-medium">
            {mode.kind === 'edit' ? mode.detail.name : ''}
            {isExternal && surname ? ` ${surname}` : ''}
          </span>
          {isExternal && (
            <span className="ml-2 inline-flex items-center rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200 text-xs font-medium px-2 py-0.5 align-middle">
              {t('admin.bookings.externalPill')}
            </span>
          )}
        </p>
      ) : (
        <label className="text-sm block">
          <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.bookings.name')}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
            required
          />
        </label>
      )}

      {isExternal && (
        <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 bg-slate-50/50 dark:bg-slate-800/40">
          <legend className="px-2 text-xs font-medium text-slate-600 dark:text-slate-400">
            {t('externalStayForm.contactSection')}
          </legend>
          <label className="text-sm block">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('externalStayForm.surname')}
            </span>
            <input
              type="text"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-sm block">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('externalStayForm.street')}
            </span>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
            />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-sm block col-span-1">
              <span className="block mb-1 text-slate-600 dark:text-slate-400">
                {t('externalStayForm.zipcode')}
              </span>
              <input
                type="text"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm block col-span-2">
              <span className="block mb-1 text-slate-600 dark:text-slate-400">
                {t('externalStayForm.town')}
              </span>
              <input
                type="text"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          <label className="text-sm block">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('externalStayForm.email')}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
            />
          </label>
        </fieldset>
      )}

      <div>
        <label className="text-sm min-w-0">
          <span className="block mb-1">
            {t('stayForm.checkIn')} – {t('stayForm.checkOut')}
          </span>
          <DateRangePicker
            from={checkIn}
            to={checkOut}
            onChange={resetDates}
            minSpanDays={1}
            required
          />
        </label>
      </div>

      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="text-left p-2 font-medium">{t('stayForm.tableHeaders.date')}</th>
              <th className="p-2 font-medium">{t('stayForm.cat.members_adult')}</th>
              <th className="p-2 font-medium">{t('stayForm.cat.members_child')}</th>
              <th className="p-2 font-medium">{t('stayForm.cat.guests_adult')}</th>
              <th className="p-2 font-medium">{t('stayForm.cat.guests_child')}</th>
              <th className="p-2 font-medium text-right">Σ</th>
            </tr>
          </thead>
          <tbody>
            {nights.map((n) => {
              const cap = capacityErrors?.[n.date];
              return (
                <tr key={n.date} className={cap ? 'bg-rose-50 dark:bg-rose-950/60' : ''}>
                  <td className="p-2 whitespace-nowrap font-medium">
                    {formatDateShort(n.date, lang)}
                    {cap && (
                      <div className="text-xs text-rose-700 dark:text-rose-300">
                        {t('stayForm.capacityDetail', {
                          requested: cap.requested,
                          available: cap.available,
                        })}
                      </div>
                    )}
                  </td>
                  {(['members_adult', 'members_child', 'guests_adult', 'guests_child'] as const).map(
                    (cat) => (
                      <td key={cat} className="p-1">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="off"
                          value={n[cat]}
                          onFocus={(e) => e.currentTarget.select()}
                          onChange={(e) =>
                            setNightField(n.date, cat, Number.parseInt(e.target.value, 10) || 0)
                          }
                          className="w-16 rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1 text-sm text-center"
                        />
                      </td>
                    ),
                  )}
                  <td className="p-2 text-right tabular-nums">{sumNight(n)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="flex items-baseline justify-between gap-2 mb-1">
            <span>{t('stayForm.stayoverChf')}</span>
            {stayoverEditedByUser && (
              <button
                type="button"
                onClick={() => setStayoverEditedByUser(false)}
                className="text-xs text-slate-500 dark:text-slate-400 underline decoration-dotted underline-offset-2"
              >
                {t('stayForm.useSuggestedAmount')}
              </button>
            )}
          </span>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]+([.,][0-9]{1,2})?"
            autoComplete="off"
            value={stayoverChf}
            onChange={(e) => {
              if (!stayoverEditedByUser) setStayoverEditedByUser(true);
              setStayoverChf(e.target.value);
            }}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1">{t('stayForm.beveragesChf')}</span>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]+([.,][0-9]{1,2})?"
            autoComplete="off"
            value={beveragesChf}
            onChange={(e) => setBeveragesChf(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="text-right text-sm tabular-nums">
        {t('admin.bookings.editTotal', { total: editTotalChf ?? '—' })}
      </div>

      <label className="text-sm block">
        <span className="block mb-1">{t('stayForm.notes')}</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
        />
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
        <span>{t('admin.bookings.overrideCapacity')}</span>
      </label>

      <fieldset className="space-y-2">
        <legend className="block text-sm font-medium mb-1">
          {t('stayForm.paymentMethod')}
        </legend>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={payTwint}
              onChange={(e) => setPayTwint(e.target.checked)}
            />
            <span>{t('stayForm.paymentTwint')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={paySlip}
              onChange={(e) => setPaySlip(e.target.checked)}
            />
            <span>{t('stayForm.paymentSlip')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={payCash}
              onChange={(e) => setPayCash(e.target.checked)}
            />
            <span>{t('stayForm.paymentCash')}</span>
          </label>
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
        <span>
          {t('admin.bookings.paid')}
          {mode.kind === 'edit' && mode.detail.paid_at && (
            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
              ({t('admin.bookings.paidOn', { date: mode.detail.paid_at })})
            </span>
          )}
        </span>
      </label>

      {err && (
        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
          {err}
        </p>
      )}

      <div className="flex justify-between gap-2 pt-2">
        {isEdit ? (
          <button
            type="button"
            onClick={() => void remove()}
            disabled={busy}
            className="rounded-md border border-rose-300 bg-white text-rose-700 dark:border-rose-700 dark:bg-slate-900 dark:text-rose-300 px-3 py-1.5 text-sm font-medium"
          >
            {t('common.delete')}
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
          >
            {isEdit ? t('common.save') : t('admin.bookings.createBooking')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Add `days` to a `YYYY-MM-DD` string, returning the same format. */
function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d! + days));
  return t.toISOString().slice(0, 10);
}
